import React, { useMemo, useState } from "react"
import {
    Accordion, AccordionDetails, AccordionSummary,
    Alert, Autocomplete, Box, Button, Chip, IconButton, MenuItem, Paper, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Typography, CircularProgress, Tooltip, CardContent
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import GroupAddIcon from "@mui/icons-material/GroupAdd"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ContentPasteIcon from "@mui/icons-material/ContentPaste"
import { useLocation, useNavigate } from "react-router-dom"
import {
    useGetExperienceCategoriesQuery,
    useCreateExperienceRequestMutation
} from "../../core/api/experience-api"
import { useGetAllUsersDetailsQuery } from "../../core/api/users-api"
import ResponseDialog from "../ResponseDialog"
import "./tokens.css"
import Card from "@mui/material/Card";

function getMyEmail() {
    try {
        const token = sessionStorage.getItem("kctoken")
        if (!token) return null
        const payload = JSON.parse(atob(token.split(".")[1]))
        return payload.email || null
    } catch { return null }
}

const rowIdFor = (email) => `row::${(email || "").toLowerCase()}`

function normalizeName(s) {
    return (s || "").toLowerCase().replace(/ё/g, "е").replace(/\s+/g, " ").trim()
}

function findEmployeeByName(fullname, employees) {
    const n = normalizeName(fullname)
    if (!n) return null
    const direct = employees.find(x => normalizeName(x.fullName || x.fullname) === n)
    if (direct) return direct
    const parts = n.split(" ")
    if (parts.length >= 2) {
        const swapped = [parts[1], parts[0]].concat(parts.slice(2)).join(" ")
        const e2 = employees.find(x => normalizeName(x.fullName || x.fullname) === swapped)
        if (e2) return e2
    }
    return null
}

// Parse "Ivanov Ivan 50" / "ivan@belka 30" / "Ivanov Ivan" into structured rows.
// Last numeric token at end of line (if any) is treated as XP.
function parseLines(raw) {
    return raw
        .split("\n")
        .map(l => l.trim())
        .filter(Boolean)
        .map(line => {
            const m = line.match(/^(.+?)\s+(\d+)\s*$/)
            const head = m ? m[1].trim() : line
            const xp = m ? parseInt(m[2], 10) : null
            const isEmail = head.includes("@")
            return isEmail
                ? { fullname: "", email: head, xp }
                : { fullname: head, email: null, xp }
        })
}

export default function CreateExperienceRequestPage() {
    const navigate = useNavigate()
    const { data: categories = [] } = useGetExperienceCategoriesQuery()
    const { data: employees = [] } = useGetAllUsersDetailsQuery()
    const [createRequest, createResponse] = useCreateExperienceRequestMutation()

    const [categoryId, setCategoryId] = useState("")
    const [comment, setComment] = useState("")
    const [bulkXp, setBulkXp] = useState(10)
    // The table is the single source of truth for participants.
    // Each row carries `source` so the live textarea sync only touches its own
    // rows and never clobbers manually-picked ones.
    const [rows, setRows] = useState([])
    const [dialogOpen, setDialogOpen] = useState(false)
    const [pasteRaw, setPasteRaw] = useState("")
    const [pasteUnmatched, setPasteUnmatched] = useState([])
    const [pasteMatched, setPasteMatched] = useState(0)

    const myEmail = getMyEmail()

    const location = useLocation()
    const inAdminSection = location.pathname.startsWith("/admin/")
    const myRequestsPath = inAdminSection ? "/admin/experience/my-requests" : "/experience/my-requests"

    React.useEffect(() => {
        if (!categoryId && categories.length) setCategoryId(categories[0].id)
    }, [categories, categoryId])

    // Selection model for the multi-select autocomplete — derived from rows.
    // Editing chips here mutates `rows`: adding chips appends rows with default XP,
    // removing chips deletes rows. XP edits inside the table are preserved across
    // chip changes (we keep existing rows untouched when their email is still in the value).
    const autocompleteValue = useMemo(
        () => rows
            .map(r => employees.find(e => e.email === r.email)
                || { email: r.email, fullName: r.fullname || r.email })
            .filter(Boolean),
        [rows, employees]
    )

    const pickedEmails = useMemo(
        () => new Set(rows.map(r => (r.email || "").toLowerCase()).filter(Boolean)),
        [rows]
    )
    const availableForRow = useMemo(
        () => employees.filter(e => e.email && e.email !== myEmail && !pickedEmails.has(e.email.toLowerCase())),
        [employees, myEmail, pickedEmails]
    )

    function handleAutocompleteChange(_, value) {
        const wantedEmails = new Set(value.map(e => (e.email || "").toLowerCase()).filter(Boolean))
        // Keep existing rows whose email is still selected (preserves their XP),
        // append new rows for newly added chips.
        const kept = rows.filter(r => wantedEmails.has((r.email || "").toLowerCase()))
        const keptEmails = new Set(kept.map(r => r.email.toLowerCase()))
        const additions = value
            .filter(e => e.email && !keptEmails.has(e.email.toLowerCase()) && e.email !== myEmail)
            .map(e => ({
                id: rowIdFor(e.email),
                fullname: e.fullName || e.fullname || "",
                email: e.email,
                xp: bulkXp,
                source: "manual",
            }))
        setRows([...kept, ...additions])
    }

    function addRowFromEmployee(emp) {
        if (!emp || !emp.email || emp.email === myEmail) return
        if (rows.some(r => r.email.toLowerCase() === emp.email.toLowerCase())) return
        setRows([...rows, {
            id: rowIdFor(emp.email),
            fullname: emp.fullName || emp.fullname || "",
            email: emp.email,
            xp: bulkXp,
            source: "manual",
        }])
    }

    function addAllEmployees() {
        const existing = new Set(rows.map(r => r.email.toLowerCase()))
        const additions = employees
            .filter(e => e.email && e.email !== myEmail && !existing.has(e.email.toLowerCase()))
            .map(e => ({
                id: rowIdFor(e.email),
                fullname: e.fullName || e.fullname || "",
                email: e.email,
                xp: bulkXp,
                source: "manual",
            }))
        setRows([...rows, ...additions])
    }

    function applyBulkXp() {
        setRows(rows.map(r => ({ ...r, xp: bulkXp })))
    }

    function updateRow(id, patch) {
        setRows(rows.map(r => r.id === id ? { ...r, ...patch } : r))
    }

    function removeRow(id) {
        setRows(rows.filter(r => r.id !== id))
    }

    // Live sync from the textarea (debounced). The textarea owns rows whose
    // `source === "textarea"`: adding a line adds a row, removing a line drops
    // the row, editing the trailing XP updates it. Manually-picked rows
    // (autocomplete, "Add all", inline add) are never touched here.
    React.useEffect(() => {
        const timer = setTimeout(() => {
            const parsed = parseLines(pasteRaw)
            const expected = new Map() // emailLower → { emp, xpFromLine }
            const unmatched = []
            for (const p of parsed) {
                let emp = null
                if (p.email) {
                    emp = employees.find(e => e.email && e.email.toLowerCase() === p.email.toLowerCase())
                        || { email: p.email, fullName: p.email }
                } else {
                    emp = findEmployeeByName(p.fullname, employees)
                }
                if (!emp || !emp.email) {
                    if ((p.fullname && p.fullname.length > 1) || p.email) {
                        unmatched.push(p.fullname || p.email)
                    }
                    continue
                }
                if (emp.email === myEmail) {
                    unmatched.push(`${p.fullname || p.email} (yourself)`)
                    continue
                }
                const key = emp.email.toLowerCase()
                if (expected.has(key)) continue
                expected.set(key, { emp, xpFromLine: p.xp })
            }

            setRows(prev => {
                const next = []
                const seen = new Set()
                for (const r of prev) {
                    const key = (r.email || "").toLowerCase()
                    const m = expected.get(key)
                    if (r.source === "textarea") {
                        if (!m) continue // line removed → drop the row
                        // Keep the row, sync XP from textarea (textarea is authoritative for its own rows).
                        next.push({
                            ...r,
                            xp: m.xpFromLine != null ? m.xpFromLine : bulkXp,
                        })
                        seen.add(key)
                    } else {
                        // Manual row: never removed by textarea. XP only nudged
                        // when the textarea line explicitly specifies a number.
                        if (m && m.xpFromLine != null) {
                            next.push({ ...r, xp: m.xpFromLine })
                        } else {
                            next.push(r)
                        }
                        seen.add(key)
                    }
                }
                // Append new textarea rows the table didn't have yet.
                for (const [key, m] of expected) {
                    if (seen.has(key)) continue
                    next.push({
                        id: rowIdFor(m.emp.email),
                        fullname: m.emp.fullName || m.emp.fullname || "",
                        email: m.emp.email,
                        xp: m.xpFromLine != null ? m.xpFromLine : bulkXp,
                        source: "textarea",
                    })
                }
                return next
            })

            setPasteMatched(expected.size)
            setPasteUnmatched(unmatched)
        }, 400)
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pasteRaw, employees, myEmail])

    const validation = useMemo(() => {
        const errors = []
        if (!comment.trim()) errors.push("Comment is required")
        if (!categoryId) errors.push("Category is required")
        if (rows.length === 0) errors.push("Participants list is empty")
        rows.forEach((r, i) => {
            if (!r.email) errors.push(`Row ${i + 1}: email is missing`)
            if (!r.xp || r.xp <= 0) errors.push(`Row ${i + 1}: XP must be > 0`)
            if (myEmail && r.email === myEmail) errors.push(`Row ${i + 1}: you can't include yourself`)
        })
        return { ok: errors.length === 0, errors }
    }, [rows, comment, categoryId, myEmail])

    async function submit() {
        const items = rows.map(r => ({ email: r.email, fullName: r.fullname, xp: parseInt(r.xp) || 0 }))
        await createRequest({ categoryId, comment: comment.trim(), items })
        setDialogOpen(true)
    }

    const totalXp = rows.reduce((s, r) => s + (parseInt(r.xp) || 0), 0)
    const totalBc = Math.floor(totalXp / 100)

    return (
        <Card sx={{ maxWidth: 1200, mx: "auto", py: 3 }}>
            <CardContent>
                <Typography variant="h4" sx={{ mb: 1 }}>XP request</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Pick a category, write a comment and add participants. Two equivalent
                    ways: dropdown picker or pasting a multi-line list. The table below
                    shows the current selection — tweak XP per row there.
                </Typography>

                <Stack spacing={2}>
                    <TextField
                        select
                        label="Category"
                        value={categoryId}
                        onChange={e => setCategoryId(e.target.value)}
                        fullWidth
                    >
                        {categories.map(c => (
                            <MenuItem key={c.id} value={c.id}>
                                {c.name}{c.description ? ` — ${c.description}` : ""}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Comment"
                        placeholder="What activity is this XP for?"
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        required
                        fullWidth
                        error={comment.length > 0 && !comment.trim()}
                    />

                    {/* Mode 1: dropdown autocomplete (multi). Chips reflect current rows;
                    add/remove chips mutates the table immediately. */}
                    <Autocomplete
                        multiple
                        options={employees.filter(e => e.email && e.email !== myEmail)}
                        getOptionLabel={(o) => o.fullName || o.fullname || o.email || ""}
                        isOptionEqualToValue={(o, v) => o.email === v.email}
                        value={autocompleteValue}
                        onChange={handleAutocompleteChange}
                        filterSelectedOptions
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Participants — pick from list"
                                placeholder="Type a surname…"
                                color="warning"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({ index })
                                return (
                                    <Chip
                                        key={option.email || key}
                                        label={option.fullName || option.fullname || option.email}
                                        size="small"
                                        {...tagProps}
                                    />
                                )
                            })
                        }
                    />

                    {/* Mode 2: paste-list textarea — live, debounced sync. Editing
                    text adds/removes/updates rows automatically (no extra button). */}
                    <PasteListPanel
                        raw={pasteRaw}
                        onRawChange={setPasteRaw}
                        matched={pasteMatched}
                        unmatched={pasteUnmatched}
                    />

                    {/* Bulk controls */}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <TextField
                            type="number"
                            label="Default XP"
                            value={bulkXp}
                            onChange={e => setBulkXp(parseInt(e.target.value) || 0)}
                            size="small"
                            sx={{ width: 160 }}
                        />
                        <Button variant="outlined" size="small" onClick={applyBulkXp} disabled={rows.length === 0}>
                            Apply to all rows
                        </Button>
                        <Tooltip title="Add every employee to the list">
                            <Button
                                variant="outlined"
                                size="small"
                                color="warning"
                                startIcon={<GroupAddIcon />}
                                onClick={addAllEmployees}
                            >
                                Add all employees
                            </Button>
                        </Tooltip>
                    </Stack>

                    {/* Result table — the single source of truth, always visible. */}
                    {rows.length > 0 ? (
                        <>
                            <Paper variant="outlined" sx={{ p: 2, display: "flex", gap: 4, flexWrap: "wrap" }}>
                                <Stat label="Participants" value={rows.length} />
                                <Stat label="Total XP" value={totalXp} />
                                <Stat label="≈ Belcoin" value={totalBc} />
                            </Paper>

                            {/* Inline single-add — quick way to append one person without
                            touching the chip list above. */}
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Autocomplete
                                        sx={{ flex: 1 }}
                                        options={availableForRow}
                                        getOptionLabel={(o) => o.fullName || o.fullname || o.email || ""}
                                        isOptionEqualToValue={(o, v) => o.email === v.email}
                                        value={null}
                                        onChange={(_, v) => addRowFromEmployee(v)}
                                        blurOnSelect
                                        clearOnBlur
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label="Add another person"
                                                placeholder="Type a surname…"
                                                color="warning"
                                            />
                                        )}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                        {availableForRow.length} left
                                    </Typography>
                                </Stack>
                            </Paper>

                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width={40}>#</TableCell>
                                            <TableCell>Surname Name</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell width={120}>XP</TableCell>
                                            <TableCell width={40} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((r, i) => {
                                            const isSelf = myEmail && r.email === myEmail
                                            return (
                                                <TableRow key={r.id} sx={{ backgroundColor: isSelf ? "var(--xp-orange-soft)" : undefined }}>
                                                    <TableCell>{i + 1}</TableCell>
                                                    <TableCell>{r.fullname}</TableCell>
                                                    <TableCell>
                                                        <span style={{ fontFamily: "monospace", fontSize: 13 }}>{r.email}</span>
                                                        {isSelf && (
                                                            <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                                                can't include yourself
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            value={r.xp}
                                                            onChange={e => updateRow(r.id, { xp: parseInt(e.target.value) || 0 })}
                                                            size="small"
                                                            inputProps={{ min: 1 }}
                                                            sx={{ width: 90 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton size="small" onClick={() => removeRow(r.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {validation.errors.length > 0 && (
                                <Alert severity="info">
                                    {validation.errors.slice(0, 5).join(" · ")}
                                    {validation.errors.length > 5 ? ` (+${validation.errors.length - 5} more)` : ""}
                                </Alert>
                            )}
                        </>
                    ) : (
                        <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
                            <Typography color="text.secondary">
                                No participants yet — pick from the list, paste a list, or click «Add all».
                            </Typography>
                        </Paper>
                    )}

                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                        <Button variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={submit}
                            disabled={!validation.ok || createResponse.isLoading}
                            startIcon={createResponse.isLoading ? <CircularProgress size={16} /> : null}
                        >
                            Submit request · {rows.length} people
                        </Button>
                    </Stack>
                </Stack>

                <ResponseDialog
                    open={dialogOpen}
                    onClose={() => {
                        setDialogOpen(false)
                        if (createResponse.isSuccess) navigate(myRequestsPath)
                    }}
                    responseData={createResponse}
                    label="XP request"
                    information="Request submitted — waiting for admin approval."
                />
            </CardContent>
        </Card>
    )
}

function Stat({ label, value, tone }) {
    const color = tone === "success" ? "var(--xp-success)" : tone === "danger" ? "var(--xp-danger)" : "var(--xp-ink)"
    return (
        <Box>
            <Typography variant="caption" sx={{ letterSpacing: ".1em", textTransform: "uppercase", color: "text.secondary" }}>
                {label}
            </Typography>
            <Typography variant="h6" sx={{ color, fontWeight: 700 }}>{value}</Typography>
        </Box>
    )
}

// Collapsible textarea for the live paste-list mode. Each change is debounced
// in the parent (CreateExperienceRequestPage) and synced into the rows array:
// added lines add rows, removed lines drop rows, trailing-XP edits update XP.
function PasteListPanel({ raw, onRawChange, matched, unmatched }) {
    return (
        <Accordion variant="outlined" disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                    <ContentPasteIcon fontSize="small" color="action" />
                    <Typography sx={{ fontWeight: 600 }}>Paste a list</Typography>
                    <Typography variant="caption" color="text.secondary">
                        — live: edit text → table updates automatically
                    </Typography>
                    {raw.trim() && (
                        <Box sx={{ ml: "auto", mr: 1 }}>
                            <Typography variant="caption" sx={{ color: unmatched.length > 0 ? "warning.main" : "success.main" }}>
                                matched {matched}{unmatched.length > 0 ? ` · unmatched ${unmatched.length}` : ""}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={1}>
                    <TextField
                        placeholder={"Ivanov Ivan 50\nPetrova Anna\nivan@belka.team 30"}
                        multiline
                        minRows={6}
                        value={raw}
                        onChange={e => onRawChange(e.target.value)}
                        fullWidth
                        sx={{ "& textarea": { fontFamily: "monospace", fontSize: 13 } }}
                        helperText="Lines auto-match by full name (surname↔firstname swap supported). Trailing number = XP for that row."
                    />
                    {unmatched.length > 0 && (
                        <Alert severity="warning" sx={{ py: 0.5 }}>
                            Unmatched: {unmatched.slice(0, 5).join(" · ")}
                            {unmatched.length > 5 ? ` (+${unmatched.length - 5} more)` : ""}
                        </Alert>
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    )
}
